package co.diena.everything.api

import kotlinx.datetime.LocalDate
import kotlinx.rpc.annotations.Rpc
import kotlinx.serialization.Serializable
import org.lortex.lines.common.GregorianDate
import org.lortex.lines.common.LongInstant
import kotlin.math.floor
import kotlin.math.log
import kotlin.math.pow

@Serializable
enum class Precision {
    Day,
    Month,
    Year,
    Decade,
    Century,
    Millenium, // 6
    Millenium10,
    Millenium100,
    MillionYears,
    MillionYears10, // 2
    MillionYears100,
    BillionYears, // 0
    ;

    fun toYearValue(): Long {
        return when (this) {
            Day -> 0L
            Month -> 0L
            Year -> 1L
            Decade -> 10L
            Century -> 100L
            Millenium -> 1000L
            Millenium10 -> 10000L
            Millenium100 -> 100000L
            MillionYears -> 1_000_000L
            MillionYears10 -> 10_000_000L
            MillionYears100 -> 100_000_000L
            BillionYears -> 1_000_000_000L
        }
    }

    companion object {
        fun makeFromWikidata(datePrecision: Int): Precision? {
            return when (datePrecision) {
                11 -> Day
                10 -> Month
                9 -> Year
                8 -> Decade
                7 -> Century
                6 -> Millenium
                5 -> Millenium10
                4 -> Millenium100
                3 -> MillionYears
                2 -> MillionYears10
                1 -> MillionYears100
                0 -> BillionYears
                else -> {
                    null
                }
            }
        }
    }
}

@Serializable
enum class DateProperty {
    Inception,
    Dissolution,
    PointInTime,
    StartTime,
    EndTime,
    DateOfBirth,
    DateOfDeath;

    companion object {
        fun makeFromWikidata(propertyId: EntityId): DateProperty? {
            if (propertyId.category != "P") {
                return null
            }
            return when (propertyId.id) {
                571 -> Inception
                576 -> Dissolution
                585 -> PointInTime
                580 -> StartTime
                582 -> EndTime
                569 -> DateOfBirth
                570 -> DateOfDeath
                else -> null
            }
        }
    }
}

@Serializable
data class EntityId(
    val category: String,
    val id: Int,
) : Comparable<EntityId> {
    override fun toString(): String {
        return "$category$id"
    }

    companion object {
        fun parse(id: String): EntityId {
            val match = Regex("([A-Z]+)(\\d+)").matchEntire(id)
                ?: throw IllegalArgumentException("Invalid entity id: $id")
            val (category, idStr) = match.destructured
            return EntityId(category, idStr.toInt())
        }
    }

    override fun compareTo(other: EntityId): Int {
        return when {
            this.category != other.category -> this.category.compareTo(other.category)
            else -> this.id.compareTo(other.id)
        }
    }
}

val CALENDAR_MODEL_GREGORIAN = EntityId.parse("Q1985727")
val CALENDAR_MODEL_JULIAN = EntityId.parse("Q1985786")

@Serializable
data class DateInfo(
    val date: GregorianDate,
    val property: DateProperty,
    val precision: Precision,
    val calendarModel: EntityId,
)

@Serializable
data class EverythingEvent(
    val id: EntityId,
    val startDate: DateInfo,
    val endDate: DateInfo,
    val categories: List<EntityId>,
    val rank: Float,
    val label: String?,
    val imageUrl: String?,
)

@Serializable
data class Bucket(
    val level: Int,
    val index: Long,
) : Comparable<Bucket> {
    override fun toString(): String {
        return "B:${level}:${index}"
    }

    override fun compareTo(other: Bucket): Int {
        return when {
            this.level != other.level -> this.level.compareTo(other.level)
            else -> this.index.compareTo(other.index)
        }
    }

    fun toDateRange(): Pair<GregorianDate, GregorianDate> {
        val bucketSize = base.pow(maxBucket - level).toLong()
        val startEpochDays = index * bucketSize
        val endEpochDays = (index + 1) * bucketSize - 1
        return GregorianDate.fromDaysSinceEpoch(startEpochDays) to GregorianDate.fromDaysSinceEpoch(
            endEpochDays
        )
    }
}

private val maxBucket = 37
private val base = 2f


private val zero = LocalDate(1, 1, 1)

fun requiredBucketsForRange(
    startDate: GregorianDate,
    endDate: GregorianDate,
    minimumBucketSize: LongInstant = LongInstant(1L),
): List<Bucket> {
    require(startDate <= endDate)
    val minimumBucketSizeDays = minimumBucketSize.value / (24 * 60 * 60).toFloat()
    val l0 = log(minimumBucketSizeDays.coerceAtLeast(0.001f), base)
    val logValue = floor(l0).toInt()
    val minBucketScale = 1
    val maxBucketScale =
        (maxBucket - logValue).coerceIn(
            minBucketScale..maxBucket
        )

    // println("Minimum bucket size: $minimumBucketSizeDays days, ($logValue / $l0) min scale: $minBucketScale, max scale: $maxBucketScale")
    return (minBucketScale..maxBucketScale).flatMap { scale ->
        val minBucket =
            (startDate.toEpochDays()) / (base.pow(maxBucket - scale)).toLong()
                .coerceAtLeast(1L)
        val maxBucket =
            (endDate.toEpochDays()) / (base.pow(maxBucket - scale)).toLong()
                .coerceAtLeast(1L)

        (minBucket..maxBucket).map {
            Bucket(scale, it)
        }
    }
}

@Serializable
data class ResolvedCategory(
    val category: EntityId,
    val label: String,
)

@Serializable
sealed interface EverythingError {
    object Unknown : EverythingError
}

@Rpc
interface EverythingApi {
    suspend fun hello()

    suspend fun availableCategories(lang: String): List<ResolvedCategory>

    suspend fun loadEvents(
        buckets: List<Bucket>,
        category: EntityId? = null,
    ): List<Pair<Bucket, EntityId>>

    suspend fun resolveEvents(
        ids: List<EntityId>,
        lang: String,
    ): List<EverythingEvent>

    suspend fun subscribeToNewsletter(email: String)

    suspend fun analyticsEventClicked(onEntityId: EntityId)
}